/**
 * Per-pod detail drawer - Phase 7 batch B (2026-05-16).
 *
 * Modal overlay that surfaces full pod telemetry + four actions:
 *   • Scale knobs - pushes a config-override patch (allowlist of 6 keys)
 *   • Diagnose    - synthesises a structured report from telemetry
 *   • Update      - records a target version as an audited INTENT (no agent
 *                   updates itself from it)
 *   • Close
 *
 * Release 3.1: the Overview says what an operator finds a device by
 * (hostname, OS, IP, signed-in user, serial, wave, business unit, check-in
 * cadence) and its health in the one vocabulary (lib/edge-health.ts). A value
 * the agent never reported reads "not reported", never blank or zero.
 *
 * Release 3.1 wave 2a: every agent type in one registry. The drawer names the
 * agent type, offers only the commands the device executes
 * (`supportedCommands`, and says why the rest are missing with `controlNote`),
 * offers the pause only when something enforces it (`pauseSupport` is not
 * 'none') and says who does, shows a gateway's fail policy and what a pause
 * does to customer traffic, and shows what a decommission did about the
 * device's credentials (EdgePodCredentialsRevocation). A refusal (409
 * command_not_supported / pause_not_supported, 423) shows the server's words.
 *
 * Receives pod id via prop; lazy-fetches the full detail on open. Each
 * action emits an idempotent POST so accidental double-clicks don't
 * push duplicate overrides.
 */
import { useRegulationText } from '@/lib/regulation-terms';
import { useEffect, useState, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  HelpCircle,
  PauseCircle,
  PlayCircle,
  Sliders,
  Stethoscope,
  Upload,
  Activity as ActivityIcon,
  Server,
  ShieldAlert,
  Send,
  Terminal,
  History,
  FileText,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ScanCoverageCard } from './ScanCoverageCard';
import { ScanResourcesCard } from './ScanResourcesCard';
import { LimitsCard } from './LimitsCard';
import { ScanHistoryTab } from './ScanHistoryTab';
import { EdgeLogsTab } from './EdgeLogsTab';
import { useCan } from '@/lib/access';
import { DEVICE_MANAGE } from '@/lib/edge-telemetry-api';
import { REMOVE_WRITE, requestAgentRemoval } from '@/lib/agent-removal-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/primitives';
import {
  getEdgePod,
  pushEdgeConfigOverride,
  diagnoseEdgePod,
  triggerEdgeUpdate,
  decommissionEdgePod,
  recommissionEdgePod,
  type EdgePodDetail,
  type DiagnoseReport,
  type DiagnoseCheckStatus,
  listEdgeCommands,
  enqueueEdgeCommand,
  type EdgeCommand,
  type EdgeCommandName,
  type EdgeCommandState,
  problemDetail,
} from '@/lib/api';
import {
  EDGE_HEALTH_VARIANT,
  checkInCadence,
  edgeDegradedReason,
  edgeHealthHint,
  edgeHealthLabel,
} from '@/lib/edge-health';
import { Trash2 } from 'lucide-react';

import { cpuTitle, formatCpu, formatRss } from './edge-measurement-format';
import { EffectiveConfigGrid } from './EffectiveConfigGrid';
import { EdgePodBusinessUnitMove } from './EdgePodBusinessUnitMove';
import { EdgePodScanHours } from './EdgePodScanHours';
import { EdgePodCredentialsRevocation } from './EdgePodCredentialsRevocation';
import {
  EDGE_COMMANDS,
  edgeAgentTypeLabel,
  edgeCommandLabel,
  gatewayPausePolicy,
  inSentence,
  offeredCommands,
  pauseEnforcerText,
  pauseNoun,
  pauseOffered,
} from '@/lib/edge-agent-types';

interface Props {
  podId: string | null;
  onClose: () => void;
  onMutated?: () => void; // Parent refreshes fleet list after mutations.
}

const COMPONENT_ICON: Record<string, typeof Server> = {
  discovery: Server,
  activity: ActivityIcon,
  detection: ShieldAlert,
};

const CHECK_VARIANT: Record<DiagnoseCheckStatus, 'success' | 'warning' | 'critical' | 'glass'> = {
  ok: 'success',
  warn: 'warning',
  fail: 'critical',
  unknown: 'glass',
};

const CHECK_ICON: Record<DiagnoseCheckStatus, typeof CheckCircle2> = {
  ok: CheckCircle2,
  warn: AlertTriangle,
  fail: XCircle,
  unknown: HelpCircle,
};

/**
 * `sent` is amber, not green. It means the command reached the device and we
 * have heard nothing back - which is the state an operator must chase, not the
 * one they can stop thinking about.
 */
const COMMAND_STATE_VARIANT: Record<EdgeCommandState, 'success' | 'warning' | 'critical' | 'glass'> = {
  queued: 'glass',
  sent: 'warning',
  acked: 'success',
  expired: 'critical',
};

interface ScaleForm {
  maxMemoryMb?: number;
  batchSize?: number;
  queueDepth?: number;
  classifierTierCap?: number;
  clickhouseHotStoreGb?: number;
  eventbusDrainIntervalMs?: number;
}

/**
 * Derive the kill-switch state shown in the drawer from the pod's active
 * config override.
 *
 * Exported so it can be tested without standing up the whole drawer - the
 * markup is not the risky part, this is. Two things it must get right:
 *
 * 1. `Object.hasOwn`, never a bare `patch['paused']`. The patch is
 *    operator-authored JSON off the wire, so a bare lookup walks the
 *    prototype chain; this repo already shipped that bug once, which is why
 *    shared/safe-lookup.ts exists.
 * 2. `=== true`, not truthiness. The value arrives as unknown from JSON, and
 *    a non-boolean must not read as paused.
 *
 * `pausePending` is only meaningful when the active override actually carries
 * a `paused` key - an unacknowledged SCALE override says nothing about the
 * kill switch and must not render "awaiting pod ack" next to it.
 */
export function deriveKillSwitchState(
  activeOverride: { patch: Record<string, unknown>; appliedAt: string | null } | null | undefined,
): { isPaused: boolean; pausePending: boolean } {
  const patch = activeOverride?.patch;
  const hasPaused = !!patch && Object.hasOwn(patch, 'paused');
  return {
    isPaused: hasPaused && patch!['paused'] === true,
    pausePending: hasPaused && !activeOverride!.appliedAt,
  };
}

/**
 * What the pause confirmation says will happen, by who enforces it. The
 * agent-enforced text keeps the delivery times an operator needs during an
 * incident; the controller-enforced text says there is nothing to deliver; a
 * gateway's adds what its fail policy does to customer traffic.
 */
export function pauseConfirmBody(
  pod: Pick<EdgePodDetail, 'agentType' | 'pauseSupport' | 'effectiveConfig'>,
  next: boolean,
): string {
  if (pod.pauseSupport === 'controller') {
    const base =
      `The controller enforces it from this device's next call (within about 2 s): ` +
      `nothing is delivered to the device and it has nothing to confirm.`;
    if (pod.agentType === 'gateway') {
      const policy = gatewayPausePolicy(pod.effectiveConfig);
      return next
        ? `${base}\n\n${policy.onPause}` +
            (policy.refusesTraffic ? ' Customer SQL through this gateway is refused until you resume it.' : '')
        : `${base}\n\nThe gateway checks queries again from its next decision call.`;
    }
    if (pod.agentType === 'dsar_connector') {
      return next
        ? `${base}\n\nIt is handed no new DSAR commands; a command already executing finishes and reports.`
        : `${base}\n\nIt is handed DSAR commands again, including any that waited while it was paused.`;
    }
    if (pod.agentType === 'agentless_collector') {
      return next
        ? `${base}\n\nNew collection runs against this target are refused and a running one stops before its next query.`
        : `${base}\n\nCollection runs against this target are accepted again.`;
    }
    return base;
  }
  if (pod.agentType === 'dam_collector') {
    return (
      `The collector reads it on its next check-in, not immediately. ` +
      (next
        ? `It then stops every reader (each keeps its place) and writes what it already read. `
        : `It then starts its readers again from where they stopped. `) +
      `The row shows "awaiting pod ack" until the collector confirms it.`
    );
  }
  return (
    `The directive is delivered on the pod's next heartbeat, not immediately. ` +
    `How long that takes depends on the agent: under a second in resident mode, ` +
    `about 5 minutes on installs from 0.0.9, and up to 4 hours on older ones ` +
    `that still ride the scan schedule. ` +
    `The row shows "awaiting pod ack" until the agent confirms it.`
  );
}

/** What a decommission also does to the device's credentials, for the prompt. */
function decommissionCredentialsNote(agentType: string | null | undefined): string {
  if (agentType === 'gateway') {
    return "It also revokes the gateway's service credential: the gateway then applies its fail policy (closed refuses customer SQL). ";
  }
  if (agentType === 'dsar_connector') {
    return "It also revokes the connector's service credential. ";
  }
  if (agentType === 'dam_collector') {
    return 'The drawer then lists the reader accounts to revoke on your own systems. ';
  }
  return '';
}

/** A value the agent reported, or a visible "not reported" (never blank, never zero). */
function Reported({ value, mono = false }: { value: string | null | undefined; mono?: boolean }) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-muted-foreground italic">not reported</span>;
  }
  return mono ? <code className="font-mono text-[11px] break-all">{value}</code> : <span>{value}</span>;
}

/**
 * A decision gateway's fail policy and what a pause does to customer traffic
 * (release 3.1 wave 2a). A paused gateway applies its fail policy exactly as
 * for an unreachable controller: closed refuses the customer's SQL, degraded
 * lets it through unchecked and records it. Said before the pause button, not
 * after it.
 */
/**
 * Release 3.2: where this device's self-update stands, as the agent last
 * reported it (effectiveConfig.update*). Read-only; the Updates page with
 * rollouts is phase 2. Nothing is shown for an agent that never reported it,
 * rather than a state it did not claim.
 */
function SelfUpdateLine({ config }: { config: Record<string, unknown> | null }) {
  const str = (k: string) => (config && typeof config[k] === 'string' ? (config[k] as string) : null);
  const state = str('updateState');
  if (!state) return null;
  const reason = str('updateStateReason');
  const target = str('updateTargetVersion');
  const previous = str('updatePreviousVersion');
  const selfUpdate = str('updateSelfUpdate');
  const trust = str('updateTrust');
  return (
    <div className="text-xs text-foreground" data-testid="update-self-state">
      <span className="text-muted-foreground">Self-update: </span>
      <code className="font-mono">{state.replace(/_/g, ' ')}</code>
      {reason ? <span className="text-muted-foreground"> ({reason.replace(/_/g, ' ')})</span> : null}
      {target ? <span> - target <code className="font-mono">{target}</code></span> : null}
      {previous ? <span> - previous <code className="font-mono">{previous}</code></span> : null}
      {selfUpdate === 'report_only' ? <span className="text-muted-foreground"> - report only on this device</span> : null}
      {trust && trust !== 'production' ? <Badge variant="destructive" className="ml-1 text-[10px]">test build</Badge> : null}
    </div>
  );
}

/**
 * Release 3.2 phase 2: this device's place in the latest agent update rollout
 * that included it (Edge Fleet > Agent updates), and what it reported for that
 * rollout's instruction.
 */
function RolloutMembershipLine({ update }: { update: EdgePodDetail['update'] }) {
  const m = update?.rollout;
  if (!m) return null;
  return (
    <div className="text-xs text-foreground" data-testid="update-rollout-membership">
      <span className="text-muted-foreground">Update rollout: </span>
      <code className="font-mono">{m.targetVersion}</code> ({m.rolloutState.replace(/_/g, ' ')}) - {m.stageLabel},{' '}
      {m.status.replace(/_/g, ' ')}
      {m.lastState ? (
        <span className="text-muted-foreground">
          {' '}- last reported {m.lastState.replace(/_/g, ' ')}
          {m.lastReason ? ` (${m.lastReason.replace(/_/g, ' ')})` : ''}
        </span>
      ) : null}
    </div>
  );
}

function GatewayPolicyCard({ effectiveConfig }: { effectiveConfig: Record<string, unknown> | null }) {
  const policy = gatewayPausePolicy(effectiveConfig);
  return (
    <GlassCard
      className={`p-3 ${policy.refusesTraffic ? 'border border-warning/40' : ''}`}
      data-testid="gateway-fail-policy"
    >
      <CardHeader className="p-0 mb-2">
        <CardTitle className="text-sm inline-flex items-center gap-1.5">
          <ShieldAlert className="h-4 w-4" /> Fail policy and pause
        </CardTitle>
        <CardDescription className="text-[11px]">
          What this gateway does to customer SQL when it cannot get a decision, and while it is paused.
        </CardDescription>
      </CardHeader>
      <dl className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
        <div>
          <dt className="text-muted-foreground">Fail policy</dt>
          <dd className="font-medium text-foreground" data-testid="gateway-fail-policy-value">
            {policy.failPolicy ?? <span className="italic font-normal text-muted-foreground">not reported</span>}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Paused (as the gateway reports it)</dt>
          <dd className="text-foreground">
            {policy.pausedNow === null
              ? <span className="italic text-muted-foreground">not reported</span>
              : policy.pausedNow ? 'yes' : 'no'}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Decision timeout</dt>
          <dd className="text-foreground">
            {policy.decisionTimeoutMs === null
              ? <span className="italic text-muted-foreground">not reported</span>
              : `${policy.decisionTimeoutMs} ms`}
          </dd>
        </div>
      </dl>
      <p
        className={`mt-2 text-xs ${policy.refusesTraffic ? 'font-medium text-foreground' : 'text-foreground'}`}
        data-testid="gateway-on-pause"
      >
        {policy.refusesTraffic ? <AlertTriangle className="mr-1 inline h-3.5 w-3.5 text-warning" /> : null}
        {policy.onPause}
      </p>
    </GlassCard>
  );
}

function OverviewField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="glass-lite rounded p-3">
      <dt className="text-muted-foreground mb-0.5">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export function EdgePodDetailDrawer({ podId, onClose, onMutated }: Props): JSX.Element {
  const tx = useRegulationText();
  const [detail, setDetail] = useState<EdgePodDetail | null>(null);
  // Release 3.3 (R3-OI-184): why it is Degraded, in plain words; null otherwise.
  const degradedWhy = detail ? edgeDegradedReason(detail) : null;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'overview' | 'history' | 'logs' | 'commands' | 'scale' | 'diagnose' | 'update'>('overview');
  // Release 3.2 telemetry item 4: the log pull is device management (IT Admin).
  const canManageDevices = useCan(DEVICE_MANAGE);
  // Release 8 item 11: ask for the agent to be removed (a different person approves).
  const canAskRemoval = useCan(REMOVE_WRITE);
  const [removalBusy, setRemovalBusy] = useState(false);
  const [removalNote, setRemovalNote] = useState<string | null>(null);

  // Command queue (Command Plane Phase 5). `null` is "not loaded yet" and is
  // rendered differently from an empty list - "no commands ever issued" and
  // "we have not looked" are different answers.
  const [commands, setCommands] = useState<EdgeCommand[] | null>(null);
  const [cmdName, setCmdName] = useState<EdgeCommandName>('pause');
  const [cmdReason, setCmdReason] = useState('');
  const [cmdBusy, setCmdBusy] = useState(false);

  // Scale-knobs form state
  const [scaleForm, setScaleForm] = useState<ScaleForm>({});
  const [pauseBusy, setPauseBusy] = useState(false);
  const [scaleBusy, setScaleBusy] = useState(false);

  // Diagnose state
  const [diagnose, setDiagnose] = useState<DiagnoseReport | null>(null);
  const [diagnoseBusy, setDiagnoseBusy] = useState(false);

  // Decommission state (Phase 0 Day 4 - 2026-05-22)
  const [decommissionBusy, setDecommissionBusy] = useState(false);

  // Update form state
  const [targetVersion, setTargetVersion] = useState('');
  // Release 3.2: an explicit rollback. Without it a self-updating agent treats
  // a version below its own as already satisfied (review M2).
  const [rollback, setRollback] = useState(false);
  const [updateBusy, setUpdateBusy] = useState(false);
  const [updateOk, setUpdateOk] = useState<string | null>(null);

  useEffect(() => {
    if (!podId) {
      setDetail(null);
      setError(null);
      setTab('overview');
      setScaleForm({});
      setDiagnose(null);
      setTargetVersion('');
      setRollback(false);
      setUpdateOk(null);
      setCommands(null);
      setCmdReason('');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await getEdgePod(podId);
        if (!cancelled) setDetail(r.pod);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'failed to load pod');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [podId]);

  async function refresh(): Promise<void> {
    if (!podId) return;
    setLoading(true);
    try {
      const r = await getEdgePod(podId);
      setDetail(r.pod);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to refresh');
    } finally {
      setLoading(false);
    }
  }

  // Release 3.1 wave 2a: the select offers only the commands this device
  // executes. When the device changes (or its list does), keep the selected
  // command one of them, so a DAM collector never opens on "rescan".
  const offeredKey = detail?.supportedCommands ? detail.supportedCommands.join(',') : '*';
  useEffect(() => {
    const offered = offeredCommands(detail?.supportedCommands);
    setCmdName((cur) => (offered.includes(cur) ? cur : (offered[0] ?? 'pause')));
    // Keyed on the device and its list, not the detail object: a refresh of
    // the same device must not reset the operator's choice.
  }, [detail?.id, offeredKey]);

  // Command history is fetched when the tab is first opened, not with the
  // drawer: most opens are to read telemetry, and a second round-trip on every
  // open would slow the common case to serve the rare one.
  useEffect(() => {
    if (tab !== 'commands' || !detail) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await listEdgeCommands(detail.podId);
        if (!cancelled) setCommands(r.commands);
      } catch (e) {
        if (!cancelled) {
          setCommands([]);
          setError(e instanceof Error ? e.message : 'failed to load command history');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [tab, detail]);

  async function handleQueueCommand(): Promise<void> {
    if (!detail) return;
    if (!offeredCommands(detail.supportedCommands).includes(cmdName)) return;
    setCmdBusy(true);
    setError(null);
    try {
      await enqueueEdgeCommand(detail.podId, {
        command: cmdName,
        reason: cmdReason.trim(),
      });
      setCmdReason('');
      const r = await listEdgeCommands(detail.podId);
      setCommands(r.commands);
      onMutated?.();
    } catch (e) {
      // A 409 command_not_supported says, in the server's words, what the
      // device does execute and why; show exactly that.
      setError(problemDetail(e, 'could not queue command'));
    } finally {
      setCmdBusy(false);
    }
  }

  /**
   * The kill switch. `paused` is one of the two spellings of the pause the
   * Rust agent enforces (`Directive::from_patch` in agent_control.rs reads
   * paused / scanEnabled), yet until now the only
   * control this drawer exposed was the six tuning knobs the schema itself
   * documents as "accepted, but NO AGENT READS THESE YET". The one control
   * that works had no button.
   *
   * Delivery is NOT instant and the confirm text says so. Overrides ride the
   * heartbeat RESPONSE - there is no separate poll - so how long this takes is
   * decided entirely by what the agent is running. Promising a single number
   * here is the exact mistake the Update tab used to make.
   *
   * The three cases, as of 2026-08-19, in the order an operator meets them:
   *
   *   - resident mode (agent 0.0.11+, opt-in): the agent holds a request open,
   *     so a command lands in well under a second. Measured on prod at 53 ms
   *     controller-side, 436 ms delivery-to-ack with a real agent.
   *   - the fast control tick (installs from 0.0.9+): a second schedule beats
   *     every 5 minutes by default without scanning.
   *   - anything installed BEFORE that: commands ride the scan schedule, which
   *     defaults to 240 minutes.
   *
   * Stating only the 4-hour case - which this copy did until today - is not
   * safely conservative, it is wrong in the direction that matters. An operator
   * who believes a pause needs four hours during an incident reaches for
   * something more drastic than pausing.
   */
  async function handleSetPaused(next: boolean): Promise<void> {
    if (!detail) return;
    if (!pauseOffered(detail.pauseSupport)) return;
    const noun = pauseNoun(detail.agentType);
    const verb = next ? `PAUSE ${noun} on` : `RESUME ${noun} on`;
    if (!window.confirm(tx(`${verb} ${detail.podId}?\n\n` + pauseConfirmBody(detail, next)))) return;
    setPauseBusy(true);
    setError(null);
    try {
      // Send `paused` explicitly either way. Absent would mean "inherit" -
      // the repository carries the prior value forward - so resuming has to
      // set false rather than omit the key.
      await pushEdgeConfigOverride(detail.podId, { paused: next });
      await refresh();
      onMutated?.();
    } catch (e) {
      // A 409 pause_not_supported explains why nothing would enforce it.
      setError(problemDetail(e, 'failed to change pause state'));
    } finally {
      setPauseBusy(false);
    }
  }

  async function handleScaleSubmit(): Promise<void> {
    if (!detail) return;
    const patch: Record<string, number> = {};
    for (const [k, v] of Object.entries(scaleForm)) {
      if (v != null && Number.isFinite(v) && v > 0) patch[k] = v;
    }
    if (Object.keys(patch).length === 0) {
      setError('Provide at least one scale knob value');
      return;
    }
    setScaleBusy(true);
    setError(null);
    try {
      await pushEdgeConfigOverride(detail.podId, patch);
      setScaleForm({});
      await refresh();
      onMutated?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to push config override');
    } finally {
      setScaleBusy(false);
    }
  }

  async function handleDiagnose(): Promise<void> {
    if (!detail) return;
    setDiagnoseBusy(true);
    setError(null);
    try {
      const r = await diagnoseEdgePod(detail.id);
      setDiagnose(r.report);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'diagnose failed');
    } finally {
      setDiagnoseBusy(false);
    }
  }

  async function handleDecommission(): Promise<void> {
    if (!detail) return;
    if (detail.decommissionedAt) return;
    const reason = window.prompt(
      `Decommission ${detail.displayName}? This stops the pod from joining further work. ` +
      decommissionCredentialsNote(detail.agentType) +
      `Enter a short reason (recorded in the audit chain):`,
      'Operator-initiated retire',
    );
    if (!reason || !reason.trim()) return;
    setDecommissionBusy(true);
    setError(null);
    try {
      const r = await decommissionEdgePod(detail.id, reason.trim());
      setDetail(r.pod);
      onMutated?.();
    } catch (e) {
      setError(problemDetail(e, 'decommission failed'));
    } finally {
      setDecommissionBusy(false);
    }
  }

  // Release 8 item 11: removing the agent itself (not only the console row)
  // needs a second person: this only ASKS; an approver decides in Edge Fleet >
  // Agent removal, and the agent then removes itself at its next check-in.
  async function handleRequestRemoval(): Promise<void> {
    if (!detail || detail.decommissionedAt) return;
    const reason = window.prompt(
      `Ask for the FOCTTA agent to be removed from ${detail.displayName}? A different person must approve it ` +
        `(Agent Management > Agent removal); the agent then uninstalls itself at its next check-in. ` +
        `Enter the reason (recorded in the audit chain):`,
      'Laptop returned to stores',
    );
    if (!reason || reason.trim().length < 3) return;
    setRemovalBusy(true);
    setError(null);
    try {
      await requestAgentRemoval(detail.id, reason.trim());
      setRemovalNote('Removal asked for. A different approver decides in Agent Management > Agent removal.');
      onMutated?.();
    } catch (e) {
      setError(problemDetail(e, 'could not ask for the removal'));
    } finally {
      setRemovalBusy(false);
    }
  }

  // Release 2.1: a decommission now stops the agent scanning for good, so it
  // needs a way back (a mistake, or a laptop back from repair).
  async function handleRecommission(): Promise<void> {
    if (!detail || !detail.decommissionedAt) return;
    const reason = window.prompt(
      `Bring ${detail.displayName} back into service? Its next check-in is accepted and it ` +
        `scans again. Enter a short reason (recorded in the audit chain):`,
      'Decommissioned by mistake',
    );
    if (!reason || reason.trim().length < 3) return;
    setDecommissionBusy(true);
    setError(null);
    try {
      const r = await recommissionEdgePod(detail.id, reason.trim());
      setDetail(r.pod);
      onMutated?.();
    } catch (e) {
      setError(problemDetail(e, 'could not bring the device back'));
    } finally {
      setDecommissionBusy(false);
    }
  }

  async function handleUpdate(): Promise<void> {
    if (!detail || !targetVersion.trim()) return;
    // Release 3.2 (docs/edge-endpoint/SELF-UPDATE.md): an agent installed by
    // the console script or the 3.2 MSI installs the requested version itself -
    // only one signed with the FOCTTA release key, inside its update window -
    // and rolls back by itself. Every other install still only records it. The
    // text says both, because which one a device is shows on the line below.
    if (!window.confirm(
      rollback
        ? `Ask this device to ROLL BACK to version ${targetVersion.trim()}? ` +
          `A self-updating agent does it only if FOCTTA has published a signed rollback from its current version ` +
          `to that one, inside its update window.`
        : `Ask this device to move to version ${targetVersion.trim()}? ` +
          `A self-updating agent installs it itself, only if it is signed with the FOCTTA release key, ` +
          `inside its update window, and puts the previous version back if the new one does not check in. ` +
          `Other installs record the request; their updates reach devices through your MDM.`,
    )) return;
    setUpdateBusy(true);
    setError(null);
    try {
      // `rollback` only when the operator asked for one (it is never a
      // standing value); a plain update sends the version alone.
      const r = await triggerEdgeUpdate(detail.podId, {
        targetVersion: targetVersion.trim(),
        ...(rollback ? { rollback: true } : {}),
      });
      setUpdateOk(`${rollback ? 'Rollback' : 'Update'} requested - etag ${r.overrideEtag.slice(0, 8)}`);
      setTargetVersion('');
      setRollback(false);
      await refresh();
      onMutated?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'update failed');
    } finally {
      setUpdateBusy(false);
    }
  }

  const open = podId !== null;
  const ComponentIcon = detail ? (COMPONENT_ICON[detail.component] ?? Server) : Server;

  const { isPaused, pausePending } = deriveKillSwitchState(detail?.activeOverride);
  // A discovery agent that scans on its own schedule (laptop agent, server
  // scanner, or a row from an older controller), as opposed to agentless
  // collection, which shares the component but has no scan hours or files.
  const scansFiles = !!detail && detail.component === 'discovery' && detail.agentType !== 'agentless_collector';
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ComponentIcon className="h-4 w-4 text-primary" />
            {detail?.displayName ?? 'Loading pod…'}
            {detail && (
              <code className="font-mono text-[11px] text-muted-foreground">
                {detail.podId.slice(0, 16)}…
              </code>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading && !detail && (
          <div className="py-12 flex items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading pod detail…
          </div>
        )}

        {error && (
          <div className="glass-lite border border-warning/40 rounded-md p-3 text-xs flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium">Action failed</div>
              <div className="text-muted-foreground mt-0.5">{error}</div>
            </div>
            <Button size="sm" variant="glass" onClick={() => setError(null)}>Dismiss</Button>
          </div>
        )}

        {detail && (
          <>
            {/* Tabs */}
            <div className="flex flex-wrap gap-2">
              {([
                ['overview', 'Overview', Server],
                ['history', 'Scan history', History],
                ...(canManageDevices ? ([['logs', 'Logs', FileText]] as const) : []),
                ['commands', 'Commands', Terminal],
                ['scale', 'Scale', Sliders],
                ['diagnose', 'Diagnose', Stethoscope],
                ['update', 'Update', Upload],
              ] as const).map(([key, label, Icon]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
                    tab === key
                      ? 'bg-primary text-primary-foreground'
                      : 'glass-lite hover:bg-muted/30'
                  }`}
                >
                  <Icon className="h-3 w-3" />
                  {label}
                </button>
              ))}
              <div className="flex-1" />
              <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            <AnimatePresence mode="wait">
              {/* Overview */}
              {tab === 'overview' && (
                <motion.div
                  key="overview"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="space-y-3"
                >
                  {/*
                    Health in the one vocabulary (lib/edge-health.ts), with
                    what it means for THIS kind of agent: a laptop is silent
                    after 24 h, a server-side agent after 2 missed check-ins.
                  */}
                  <div className="glass-lite rounded p-3 text-xs" data-testid="drawer-health">
                    <div className="text-muted-foreground mb-0.5">Health</div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        variant={(EDGE_HEALTH_VARIANT as Record<string, 'success' | 'warning' | 'critical' | 'glass'>)[detail.healthStatus] ?? 'glass'}
                        className="text-[10px]"
                      >
                        {edgeHealthLabel(detail.healthStatus)}
                      </Badge>
                      <span className="text-muted-foreground">
                        {degradedWhy
                          ? `Reason: ${degradedWhy.label}`
                          : edgeHealthHint(detail.healthStatus, detail.agentClass, detail.healthReason)}
                      </span>
                    </div>
                    {/*
                      Release 3.3 (R3-OI-184): the specific reason, with its
                      next step and the agent's own sentence, instead of only
                      "the agent reports a fault".
                    */}
                    {degradedWhy && (
                      <div className="mt-1.5 space-y-0.5" data-testid="drawer-degraded-reason">
                        <div>
                          <span className="text-muted-foreground">Next step: </span>
                          {degradedWhy.nextStep}
                        </div>
                        {degradedWhy.detail && (
                          <div className="text-muted-foreground">
                            The agent says: <span className="text-foreground">{degradedWhy.detail}</span>
                          </div>
                        )}
                        <code className="font-mono text-[10px] text-muted-foreground">{degradedWhy.code}</code>
                      </div>
                    )}
                  </div>

                  {/*
                    Release 3.1 wave 2a: what the decommission did about the
                    credentials this device read with (revoked by the
                    controller, or to be revoked by you and confirmed here).
                  */}
                  <EdgePodCredentialsRevocation
                    pod={detail}
                    onConfirmed={(p) => {
                      setDetail(p);
                      onMutated?.();
                    }}
                  />

                  <dl className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs" data-testid="drawer-identity">
                    <OverviewField label="Type">
                      <span data-testid="drawer-agent-type">{tx(edgeAgentTypeLabel(detail))}</span>
                      <span className="text-muted-foreground"> - {detail.component}</span>
                    </OverviewField>
                    <OverviewField label="Hostname"><Reported value={detail.hostname} mono /></OverviewField>
                    <OverviewField label="OS">
                      <Reported value={detail.osName ? `${detail.osName}${detail.osVersion ? ` ${detail.osVersion}` : ''}` : null} />
                    </OverviewField>
                    <OverviewField label="IP address"><Reported value={detail.primaryIp} mono /></OverviewField>
                    <OverviewField label="Signed-in user"><Reported value={detail.loggedInUser} /></OverviewField>
                    <OverviewField label="Serial number"><Reported value={detail.serialNumber} mono /></OverviewField>
                    <OverviewField label="Instance id">
                      <code className="font-mono text-[11px] break-all">{detail.podId}</code>
                    </OverviewField>
                    <OverviewField label="Wave">
                      {detail.installTokenId ? (
                        <code className="font-mono text-[11px]" title={detail.installTokenId}>
                          Wave {detail.installTokenId.slice(0, 8)}
                        </code>
                      ) : (
                        <Reported value={null} />
                      )}
                    </OverviewField>
                    <OverviewField label="Business unit">
                      {detail.businessUnitId === null ? 'No unit' : <Reported value={detail.businessUnitName} />}
                    </OverviewField>
                    <OverviewField label="Enrolled">{new Date(detail.createdAt).toLocaleString()}</OverviewField>
                    <OverviewField label="Check-in cadence">
                      {checkInCadence(detail.livenessIntervalSeconds) === 'not reported'
                        ? <Reported value={null} />
                        : checkInCadence(detail.livenessIntervalSeconds)}
                    </OverviewField>
                    <OverviewField label="Last check-in">
                      {detail.lastHeartbeatAt ? new Date(detail.lastHeartbeatAt).toLocaleString() : 'never'}
                    </OverviewField>
                    <OverviewField label="Version"><code className="font-mono text-[11px]">{detail.version}</code></OverviewField>
                    <OverviewField label="CPU (of one core) / memory at check-in">
                      <span className="font-mono text-[11px]" title={cpuTitle(detail)}>
                        {formatCpu(detail)} / {formatRss(detail.rssMb)}
                      </span>
                    </OverviewField>
                    {(detail.helmChartVersion || detail.clusterName || detail.namespace) && (
                      <OverviewField label="Helm chart / cluster">
                        <code className="font-mono text-[11px]">
                          {detail.helmChartVersion ?? '-'}
                          {detail.clusterName ? ` - ${detail.clusterName}` : ''}
                          {detail.namespace ? ` / ${detail.namespace}` : ''}
                        </code>
                      </OverviewField>
                    )}
                  </dl>

                  <div className="grid grid-cols-3 gap-3 text-xs">
                    <div className="glass-lite rounded p-3">
                      <div className="text-muted-foreground mb-0.5">Events 24h</div>
                      <div className="tabular text-sm">{detail.eventsProcessed24h.toLocaleString('en-IN')}</div>
                    </div>
                    <div className="glass-lite rounded p-3">
                      <div className="text-muted-foreground mb-0.5">Drops 24h</div>
                      <div className={`tabular text-sm ${detail.eventsDropped24h > 0 ? 'text-sev-critical' : ''}`}>
                        {detail.eventsDropped24h.toLocaleString('en-IN')}
                      </div>
                    </div>
                    <div className="glass-lite rounded p-3">
                      <div className="text-muted-foreground mb-0.5">Lag</div>
                      <div className="tabular text-sm">
                        {detail.lagWatermarkSeconds == null ? '-' : `${detail.lagWatermarkSeconds}s`}
                      </div>
                    </div>
                    {detail.component === 'detection' && (
                      <>
                        <div className="glass-lite rounded p-3">
                          <div className="text-muted-foreground mb-0.5">Decision p50</div>
                          <div className="tabular text-sm">
                            {detail.decisionRpcP50Ms == null ? '-' : `${detail.decisionRpcP50Ms}ms`}
                          </div>
                        </div>
                        <div className="glass-lite rounded p-3">
                          <div className="text-muted-foreground mb-0.5">Decision p95</div>
                          <div className="tabular text-sm">
                            {detail.decisionRpcP95Ms == null ? '-' : `${detail.decisionRpcP95Ms}ms`}
                          </div>
                        </div>
                        <div className="glass-lite rounded p-3">
                          <div className="text-muted-foreground mb-0.5">Decision p99</div>
                          <div className="tabular text-sm">
                            {detail.decisionRpcP99Ms == null ? '-' : `${detail.decisionRpcP99Ms}ms`}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/*
                    Release 2.1 item 10: what the last scan did not read, and
                    why. A file scan of a device: not agentless collection,
                    which the controller runs against a database or a CMDB.
                  */}
                  {scansFiles && (
                    <ScanCoverageCard files={detail.lastScanFiles} finishedAt={detail.lastScanFinishedAt} />
                  )}

                  {/*
                    Release 3.2 telemetry item 2: what the last scan cost the
                    machine, measured while it ran, against the CPU ceiling.
                  */}
                  {scansFiles && (
                    <ScanResourcesCard resources={detail.lastScanResources} finishedAt={detail.lastScanFinishedAt} />
                  )}

                  {/* Release 3.2 telemetry item 5: every limit, sent beside what the agent confirms. */}
                  <LimitsCard rows={detail.limits} />

                  {/*
                    THE KILL SWITCH. Until 2026-08-10 this drawer's only control was the
                    six scale knobs, which the API schema itself documents as "accepted,
                    but NO AGENT READS THESE YET" - so the one directive agents DO
                    enforce had no button, and "we can stop it remotely" was not
                    something an operator could actually do from the console.
                  */}
                  {/*
                    Release 3.1 wave 2a: a decision gateway's fail policy and
                    what a pause does to customer traffic, from what the
                    gateway reports (effectiveConfig failPolicy / onPause).
                  */}
                  {detail.agentType === 'gateway' && (
                    <GatewayPolicyCard effectiveConfig={detail.effectiveConfig} />
                  )}

                  {pauseOffered(detail.pauseSupport) ? (
                    <GlassCard className="p-3" data-testid="pause-control">
                      <CardHeader className="p-0 mb-2">
                        <CardTitle className="text-sm inline-flex items-center gap-1.5">
                          <PauseCircle className="h-4 w-4" />{' '}
                          {scansFiles
                            ? 'Scanning control'
                            : 'Pause control'}
                        </CardTitle>
                        <CardDescription className="text-[11px]">
                          {detail.pauseSupport === 'controller'
                            ? <>The controller refuses this device&apos;s calls while it is paused.</>
                            : detail.agentType === 'dam_collector'
                              ? <>Read by the collector itself (release 3.1) on its next check-in: it
                                  stops every reader and writes what it already read.</>
                              : <>Delivered on the pod&apos;s next heartbeat, not immediately. How long
                                  that takes is set by the agent: under a second in resident mode,
                                  ~5 minutes on installs from 0.0.9, up to 4 hours on older ones.</>}
                        </CardDescription>
                      </CardHeader>
                      <p className="mb-2 text-[11px] text-foreground" data-testid="pause-enforcer">
                        {pauseEnforcerText(detail.pauseSupport)}
                      </p>
                      {detail.controlNote && (
                        <p className="mb-2 text-[11px] text-muted-foreground" data-testid="control-note">
                          {detail.controlNote}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-3">
                        <Badge variant={isPaused ? 'critical' : 'success'}>
                          {isPaused
                            ? 'Paused by operator'
                            : pauseNoun(detail.agentType) === 'scanning'
                              ? 'Scanning enabled'
                              : 'Running'}
                        </Badge>
                        <Button
                          size="sm"
                          variant={isPaused ? 'success' : 'destructive'}
                          disabled={pauseBusy || !!detail.decommissionedAt}
                          onClick={() => void handleSetPaused(!isPaused)} data-action="disc.adm_edge_fleet.a02"
                        >
                          {pauseBusy
                            ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                            : isPaused
                              ? <PlayCircle className="h-3 w-3 mr-1.5" />
                              : <PauseCircle className="h-3 w-3 mr-1.5" />}
                          {tx(isPaused ? `Resume ${pauseNoun(detail.agentType)}` : `Pause ${pauseNoun(detail.agentType)}`)}
                        </Button>
                        {/*
                          A controller-enforced pause is in force at once: the
                          device never acknowledges it, so "awaiting pod ack"
                          would never clear and would read as a fault.
                        */}
                        {pausePending && detail.pauseSupport !== 'controller' && (
                          <span className="text-[11px] text-warning">awaiting pod ack</span>
                        )}
                        {isPaused && detail.pauseSupport === 'controller' && (
                          <span className="text-[11px] text-foreground">in force (enforced by the controller)</span>
                        )}
                      </div>
                    </GlassCard>
                  ) : (
                    <GlassCard className="p-3" data-testid="pause-not-offered">
                      <CardHeader className="p-0 mb-2">
                        <CardTitle className="text-sm inline-flex items-center gap-1.5">
                          <PauseCircle className="h-4 w-4" /> Pause not available
                        </CardTitle>
                        <CardDescription className="text-[11px]">
                          {pauseEnforcerText('none')}
                        </CardDescription>
                      </CardHeader>
                      {detail.controlNote && (
                        <p className="text-[11px] text-foreground" data-testid="control-note">{detail.controlNote}</p>
                      )}
                    </GlassCard>
                  )}
                  {/* Release 3.0.1: the hours a scheduled scan may run in (read by the scanner agent). */}
                  {scansFiles && (
                    <EdgePodScanHours
                      pod={detail}
                      onSaved={async () => {
                        await refresh();
                        onMutated?.();
                      }}
                    />
                  )}
                  {/*
                    Moving a laptop to another business unit (founder decision,
                    22 September 2026): the only way a device changes unit. Its
                    findings move with it; the control is disabled, with the
                    reason, for an operator without authority over both units.
                  */}
                  <EdgePodBusinessUnitMove
                    pod={detail}
                    onMoved={(p) => {
                      setDetail(p);
                      onMutated?.();
                    }}
                  />
                  {detail.activeOverride && (
                    <GlassCard className="p-3">
                      <CardHeader className="p-0 mb-2">
                        <CardTitle className="text-sm">Active config override</CardTitle>
                        <CardDescription className="text-[11px]">
                          etag <code className="font-mono">{detail.activeOverride.overrideEtag}</code>
                          {' '}pushed {new Date(detail.activeOverride.pushedAt).toLocaleString()}
                          {detail.activeOverride.appliedAt
                            ? <> · applied {new Date(detail.activeOverride.appliedAt).toLocaleString()}</>
                            : <> · <span className="text-warning">awaiting pod ack</span></>}
                        </CardDescription>
                      </CardHeader>
                      <pre className="text-[11px] font-mono">
                        {JSON.stringify(detail.activeOverride.patch, null, 2)}
                      </pre>
                    </GlassCard>
                  )}

                  {/*
                    WHAT WE ASKED FOR vs WHAT IS RUNNING. Everything above this
                    point is desired state. Those two diverge whenever a push
                    failed, a local file overrode it, or the agent predates the
                    setting - and an operator reading desired state as though it
                    were actual concludes a scan is paused while it is still
                    running. The null case is spelled out rather than rendered
                    as defaults for exactly that reason.
                  */}
                  <GlassCard className="p-3">
                    <CardHeader className="p-0 mb-2">
                      <CardTitle className="text-sm">Actually running</CardTitle>
                      <CardDescription className="text-[11px]">
                        {detail.effectiveConfigAt
                          ? <>Self-reported by the agent {new Date(detail.effectiveConfigAt).toLocaleString()}</>
                          : <>Not reported by this agent</>}
                      </CardDescription>
                    </CardHeader>
                    {detail.effectiveConfig ? (
                      <EffectiveConfigGrid config={detail.effectiveConfig} />
                    ) : (
                      <div className="text-[11px] text-muted-foreground">
                        This agent has never reported its running configuration - it predates
                        the report (agent 0.0.9+). That is <strong>not</strong> the same as
                        running defaults, so nothing is shown here rather than a guess.
                      </div>
                    )}
                  </GlassCard>
                </motion.div>
              )}

              {/* Commands (Command Plane Phase 5) */}
              {/* Release 3.2 telemetry item 4: pull the device's recent log (IT Admin only). */}
              {tab === 'logs' && canManageDevices && (
                <motion.div
                  key="logs"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="space-y-3"
                >
                  <EdgeLogsTab
                    podRowId={detail.id}
                    logPull={detail.logPull === true}
                    decommissioned={detail.decommissionedAt !== null}
                  />
                </motion.div>
              )}

              {/* Release 3.2 telemetry item 3: every scan run this device reported. */}
              {tab === 'history' && (
                <motion.div
                  key="history"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="space-y-3"
                >
                  <ScanHistoryTab podRowId={detail.id} />
                </motion.div>
              )}

              {tab === 'commands' && (
                <motion.div
                  key="commands"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="space-y-3"
                >
                  <div className="text-xs text-muted-foreground">
                    Commands are imperatives with a per-command acknowledgement, separate from
                    the config override above (which carries desired state and is last-write-wins).
                    They ride the pod&apos;s next heartbeat, so an ack is not immediate - but how
                    long depends on the agent: under a second in resident mode (0.0.11+, holds a
                    request open), ~5 minutes on installs from 0.0.9, and up to 4 hours on older
                    ones that still ride the scan schedule.
                  </div>

                  {offeredCommands(detail.supportedCommands).length === 0 ? (
                    <div className="glass-lite rounded p-3 text-xs space-y-1" data-testid="commands-none">
                      <div className="font-medium text-foreground">
                        This {tx(inSentence(edgeAgentTypeLabel(detail)))} takes no queued commands.
                      </div>
                      {detail.controlNote && <div className="text-muted-foreground">{detail.controlNote}</div>}
                    </div>
                  ) : (
                  <GlassCard className="p-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label htmlFor="edge-command-name" className="text-xs text-muted-foreground block mb-0.5">Command</label>
                        <select
                          id="edge-command-name"
                          value={cmdName}
                          onChange={(e) => setCmdName(e.target.value as EdgeCommandName)}
                          className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
                        >
                          {offeredCommands(detail.supportedCommands).map((c) => (
                            <option key={c} value={c}>{edgeCommandLabel(c, detail.agentType)}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground block mb-0.5">
                          Reason (required)
                        </label>
                        <input
                          type="text"
                          value={cmdReason}
                          onChange={(e) => setCmdReason(e.target.value)}
                          placeholder="suspected runaway scan"
                          maxLength={500}
                          className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40 focus:border-primary focus:outline-none"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] text-muted-foreground">
                        The reason lands in the audit chain, not just the row. &quot;Who stopped
                        this laptop and why&quot; is asked a year later, by someone who cannot
                        read the table.
                      </span>
                      <Button
                        size="sm"
                        onClick={() => void handleQueueCommand()} data-action="disc.adm_edge_fleet.a01"
                        disabled={cmdBusy || cmdReason.trim().length < 3 || !!detail.decommissionedAt}
                      >
                        {cmdBusy
                          ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                          : <Send className="h-3.5 w-3.5 mr-1" />}
                        Queue command
                      </Button>
                    </div>
                    {/*
                      Release 3.1 wave 2a: the commands this device does not
                      execute are not offered; say which, and why.
                    */}
                    {Array.isArray(detail.supportedCommands) &&
                      EDGE_COMMANDS.some((c) => !offeredCommands(detail.supportedCommands).includes(c)) && (
                        <div className="text-[11px] text-muted-foreground" data-testid="commands-hidden">
                          Not offered, because this {tx(inSentence(edgeAgentTypeLabel(detail)))} does not execute them:{' '}
                          {EDGE_COMMANDS.filter((c) => !offeredCommands(detail.supportedCommands).includes(c))
                            .map((c) => inSentence(edgeCommandLabel(c, detail.agentType)))
                            .join(', ')}
                          .{detail.controlNote ? ` ${detail.controlNote}` : ''}
                        </div>
                      )}
                  </GlassCard>
                  )}

                  {commands === null ? (
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading command history…
                    </div>
                  ) : commands.length === 0 ? (
                    <div className="text-xs text-muted-foreground">
                      No commands have been issued to this instance.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {commands.map((c) => (
                        <div key={c.id} className="glass-lite rounded p-2 text-xs">
                          <div className="flex items-center gap-2 flex-wrap">
                            <code className="font-mono">{c.command}</code>
                            <Badge variant={COMMAND_STATE_VARIANT[c.state] ?? 'glass'} className="text-[10px]">
                              {c.state}
                            </Badge>
                            {c.result && (
                              <Badge
                                variant={c.result === 'ok' ? 'success' : 'critical'}
                                className="text-[10px]"
                              >
                                {c.result}
                              </Badge>
                            )}
                            <span className="ml-auto text-[10px] text-muted-foreground">
                              {new Date(c.createdAt).toLocaleString()}
                            </span>
                          </div>
                          {c.reason && (
                            <div className="text-muted-foreground mt-1">{c.reason}</div>
                          )}
                          {c.resultDetail && (
                            <pre className="text-[10px] font-mono mt-1 whitespace-pre-wrap break-all text-muted-foreground">
                              {c.resultDetail}
                            </pre>
                          )}
                          {/*
                            `sent` and `acked` are deliberately distinct states.
                            A command that reached a laptop which then died must
                            be distinguishable from one never delivered - those
                            need different operator responses.
                          */}
                          {c.state === 'sent' && (
                            <div className="text-[10px] text-warning mt-1">
                              delivered {c.sentAt ? new Date(c.sentAt).toLocaleString() : ''} · awaiting outcome
                              {/*
                                Repeated delivery with no ack is a different
                                problem from no delivery: the device is taking
                                commands and not answering. Retries stop after
                                5, so a stuck one says so rather than looking
                                like it is still being tried.
                              */}
                              {c.deliveryAttempts > 1 && (
                                <> · delivered {c.deliveryAttempts}x with no answer
                                  {c.deliveryAttempts >= 5 ? ' (retries exhausted)' : ''}</>
                              )}
                            </div>
                          )}
                          {c.state === 'expired' && (
                            <div className="text-[10px] text-muted-foreground mt-1">
                              never collected - the instance did not heartbeat before it expired
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {/* Scale */}
              {tab === 'scale' && (
                <motion.div
                  key="scale"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="space-y-3"
                >
                  <div className="text-xs text-muted-foreground">
                    Push a Helm-values patch. Only the 6 keys below are accepted; everything else is
                    rejected server-side. The pod picks up the override on its next config-override
                    poll (≤30s).
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {([
                      ['maxMemoryMb', 'Max memory (MiB)', 64, 65536, 'Container memory limit'],
                      ['batchSize', 'Batch size', 1, 10000, 'Events per write batch'],
                      ['queueDepth', 'Queue depth', 1, 100000, 'Internal queue capacity'],
                      ['classifierTierCap', 'Classifier tier cap', 1, 5, 'Highest classifier tier to run (1=Tier1 only, 5=all)'],
                      ['clickhouseHotStoreGb', 'ClickHouse hot store (GiB)', 1, 2048, 'Per-pod hot-store hint'],
                      ['eventbusDrainIntervalMs', 'Eventbus drain (ms)', 10, 60000, 'Drain cadence'],
                    ] as const).map(([key, label, min, max, helper]) => (
                      <div key={key}>
                        <label className="text-xs text-muted-foreground block mb-0.5">{label}</label>
                        <input
                          type="number"
                          min={min}
                          max={max}
                          value={scaleForm[key] ?? ''}
                          onChange={(e) => setScaleForm((s) => ({
                            ...s,
                            [key]: e.target.value === '' ? undefined : Number(e.target.value),
                          }))}
                          placeholder={`${min}-${max}`}
                          className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40 focus:border-primary focus:outline-none"
                        />
                        <div className="text-[10px] text-muted-foreground mt-0.5">{helper}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end">
                    <Button size="sm" onClick={() => void handleScaleSubmit()} data-action="disc.adm_edge_fleet.a03" disabled={scaleBusy}>
                      {scaleBusy
                        ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        : <Sliders className="h-3.5 w-3.5 mr-1" />}
                      Push override
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Diagnose */}
              {tab === 'diagnose' && (
                <motion.div
                  key="diagnose"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">
                      Runs a diagnostic check against the pod's last-known telemetry. When edge
                      components ship the pod-side /diagnose endpoint, this becomes a live RPC.
                    </div>
                    <Button size="sm" onClick={() => void handleDiagnose()} data-action="disc.adm_edge_fleet.a04" disabled={diagnoseBusy}>
                      {diagnoseBusy
                        ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        : <Stethoscope className="h-3.5 w-3.5 mr-1" />}
                      Run diagnostic
                    </Button>
                  </div>

                  {diagnose && (
                    <GlassCard className="p-3">
                      <CardHeader className="p-0 mb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          Diagnostic report
                          <Badge variant={CHECK_VARIANT[diagnose.overall]} className="text-[10px]">
                            {diagnose.overall.toUpperCase()}
                          </Badge>
                          <Badge variant="glass" className="text-[10px]">
                            {diagnose.mode === 'synthesised_from_telemetry' ? 'synthesised' : 'live'}
                          </Badge>
                        </CardTitle>
                        <CardDescription className="text-[11px]">{diagnose.summary}</CardDescription>
                      </CardHeader>
                      <div className="space-y-2">
                        {diagnose.checks.map((c, i) => {
                          const Icon = CHECK_ICON[c.status];
                          return (
                            <motion.div
                              key={c.name}
                              initial={{ opacity: 0, x: -4 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.04 }}
                              className="glass-lite rounded p-2 flex items-start gap-2 text-xs"
                            >
                              <Icon className={`h-3.5 w-3.5 shrink-0 mt-0.5 ${
                                c.status === 'ok' ? 'text-success'
                                  : c.status === 'warn' ? 'text-warning'
                                  : c.status === 'fail' ? 'text-sev-critical'
                                  : 'text-muted-foreground'
                              }`} />
                              <div className="flex-1">
                                <div className="font-medium">{c.name}</div>
                                <div className="text-muted-foreground mt-0.5">{c.detail}</div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </GlassCard>
                  )}
                </motion.div>
              )}

              {/* Update */}
              {tab === 'update' && (
                <motion.div
                  key="update"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="space-y-3"
                >
                  <div className="text-xs text-muted-foreground" data-testid="update-intent-text">
                    For more than a device or two, use Agent updates on the Agent Management page (a
                    pilot, then waves, with an automatic halt and a second person's approval).
                    This one-device request needs the approver permission, and is accepted only
                    for a rollback (a version below this device's that it ran before) or for a
                    version an approved rollout already targets (any other
                    version: create a rollout, a one-device pilot is fine, so a second person
                    approves it); it is refused while agent updates are on hold. Ask this device to move to a version; the request is in the audit log. An
                    agent installed by the console script or the 3.2 MSI installs it itself:
                    only a version signed with the FOCTTA release key, inside its update window
                    (default 20:00-08:00), keeping the previous version and restoring it by
                    itself if the new one does not check in. Other installs (older agents,
                    machines set to report only, containers) record the request; their updates
                    reach devices through your MDM (for example Intune).
                  </div>
                  <SelfUpdateLine config={detail.effectiveConfig} />
                  <RolloutMembershipLine update={detail.update ?? null} />
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="edge-target-version" className="text-xs text-muted-foreground block mb-0.5">
                        Target version
                      </label>
                      <input
                        id="edge-target-version"
                        type="text"
                        value={targetVersion}
                        onChange={(e) => setTargetVersion(e.target.value)}
                        placeholder="0.2.0"
                        maxLength={32}
                        className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40 focus:border-primary focus:outline-none"
                      />
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        Current: <code className="font-mono">{detail.version}</code>
                      </div>
                    </div>
                    <label className="flex items-start gap-2 text-xs text-muted-foreground self-center">
                      <input
                        type="checkbox"
                        checked={rollback}
                        onChange={(e) => setRollback(e.target.checked)}
                        className="mt-0.5"
                      />
                      <span>
                        Roll back to this older version. Needs a signed rollback from FOCTTA;
                        without this, a lower version is treated as already reached.
                      </span>
                    </label>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    {updateOk
                      ? <Badge variant="success" className="text-[10px]"><Clock className="h-3 w-3 mr-1" />{updateOk}</Badge>
                      : <div />}
                    <Button
                      size="sm"
                      onClick={() => void handleUpdate()} data-action="disc.adm_edge_fleet.a07"
                      disabled={updateBusy || !targetVersion.trim()}
                    >
                      {updateBusy
                        ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        : <Upload className="h-3.5 w-3.5 mr-1" />}
                      Request update
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        {removalNote && (
          <div className="text-xs text-muted-foreground pt-2" data-testid="removal-asked">{removalNote}</div>
        )}
        <div className="flex items-center justify-between pt-2 border-t border-border/20">
          {detail && !detail.decommissionedAt ? (
            <div className="flex items-center gap-2">
            {canAskRemoval && (
              <Button
                size="sm"
                variant="glass"
                onClick={() => void handleRequestRemoval()}
                disabled={removalBusy || detail.agentRemoval !== true}
                title={
                  detail.agentRemoval === true
                    ? 'Ask for the agent to be uninstalled from this device. A different approver decides.'
                    : 'This agent cannot remove itself: only agents from release 8 installed by the FOCTTA MSI can. An older agent carries no removal check and is uninstalled on the laptop the way it was installed.'
                }
              >
                {removalBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
                Remove agent
              </Button>
            )}
            <Button
              size="sm"
              variant="destructive"
              onClick={() => void handleDecommission()} data-action="disc.adm_edge_fleet.a05"
              disabled={decommissionBusy}
              title="Retire this pod row. Audit-chained."
            >
              {decommissionBusy ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5 mr-1" />
              )}
              Decommission
            </Button>
            </div>
          ) : detail?.decommissionedAt ? (
            <div className="flex items-center gap-2">
              <Badge variant="glass" className="text-[10px]">
                Decommissioned · {new Date(detail.decommissionedAt).toLocaleString()}
              </Badge>
              <Button
                size="sm"
                variant="glass"
                onClick={() => void handleRecommission()} data-action="disc.adm_edge_fleet.a06"
                disabled={decommissionBusy}
                title="Accept this device's check-ins again. Audit-chained."
              >
                {decommissionBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
                Bring back into service
              </Button>
            </div>
          ) : (
            <span />
          )}
          <Button size="sm" variant="glass" onClick={onClose}>
            <X className="h-3.5 w-3.5 mr-1" />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
